import { Component, OnInit, Input, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProducerService } from '../producer.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ApplicationRecord } from '../interfaces/application-record.interface';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { SearchParams } from '../interfaces/search-params.interface';
import { SearchResponse } from '../interfaces/search-response.interface';
import { DateService } from 'src/app/shared/services/date.service';
import { UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME, LoggedInUserInformationService } from '@ptl/account-management';
import { BooleanInput, GlobalShadowStyleDirective, JsonInput } from '@ptl/global-components';
import { ApplicationStatusRetrievalService } from 'src/app/shared/services/application-status-retrieval.service';
import { ReducerResetService } from '../../shared/reducer-reset.service';
import { D3GraphService } from './d3-graph.service';
import { APP_STATUS, STATUS_MAP } from '../enums/app-status.enum';
import { environment } from 'src/environments/environment';
import { QuickLink } from '../interfaces/dynamic-link.interface';
import { AppStatus } from '../interfaces/app-status.interface';


@Component({
    selector: 'app-producer-dashboard',
    templateUrl: './producer-dashboard.component.html',
    styleUrls: ['./producer-dashboard.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class ProducerDashboardComponent extends GlobalShadowStyleDirective implements OnInit {
  @Input() marketType = 'all';

  @Input() retrieveUrl: string;
  @Input() viewUrl: string;

  @Input() @BooleanInput() hideQuickLinks: boolean = false;
  @Input() @JsonInput() quickLinksArray: QuickLink[] = [];

  maxDate = new Date(); // Restricting user to select future date
  plans: ApplicationRecord[] = [];
  planSearchMessage = '';
  firstName = '';
  userId = '';
  isAgentCube = false;
  agencyId = '';

  donutChartElementId = 'application-donut-chart';

  sortList = [
    'Most Recently Submitted',
    'Most Recently Created',
    'Name (Ascending)',
    'Name (Descending)'
  ];

  DEFAULT_PLANS_PER_PAGE = 10;

  sortOption = 'Most Recently Created';
  noOfPlans = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  plansPerPage = this.DEFAULT_PLANS_PER_PAGE;
  currentPage = 1;
  totalApplications = 0;
  totalPages = 0;
  firstAppNo = 1;
  lastAppNo = this.plansPerPage;

  graphDataSource: BehaviorSubject<AppStatus> = new BehaviorSubject<AppStatus>({} as AppStatus);
  graphData$ = this.graphDataSource.asObservable();
  statuses = [];
  showFilterOptions = false;
  searchForm: UntypedFormGroup;
  plansPerPageForm: UntypedFormGroup;
  appNumber: string;
  searchParams: SearchParams;

  constructor(
    private fb: UntypedFormBuilder,
    private producerService: ProducerService,
    private reducerUtilityService: ReducerUtilityService,
    private dateUtility: DateService,
    private loggedInUserService: LoggedInUserInformationService,
    private appStatusRetrieveService: ApplicationStatusRetrievalService,
    private reducerResetService: ReducerResetService,
    private d3Graph: D3GraphService
  ) {
    super();

    this.searchForm = this.fb.group({
      firstName: this.fb.control('', []),
      lastName: this.fb.control('', []),
      planName: this.fb.control('', []),
      status: this.fb.control('', []),
      fromDate: this.fb.control('', []),
      toDate: this.fb.control('', []),
    });

    this.plansPerPageForm = this.fb.group({
      plansPerPageFormControl: this.fb.control(10, { nonNullable: true })
    });

    this.graphData$.subscribe((statusData: AppStatus) => {
      if (statusData) {
        const statusDataLabeled = this.statusDataMapLabels(statusData)
        this.d3Graph.drawProducerDonutGraph(statusDataLabeled, this.donutChartElementId);
      }
    });
  }

  ngOnInit() {
    this.reducerResetService.resetPayloadStates();
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const lob = this.reducerUtilityService.getApplicationStates().Lob.lob;
    this.isAgentCube = this.loggedInUserService.isAgentCube();

    this.showFilterOptions = false;
    this.userId = loginInfo.userId;
    this.firstName = loginInfo.basicUserInfo.firstName;
    this.agencyId = loginInfo.userInfo.agencyInfo.selectedAgencies[lob].agencyId
    this.search(true);
    this.getApplicationStatusCount(loginInfo.userId);
  }

  async getApplicationStatusCount(userId: string) {
    // TODO: is a try/catch really necessary here?
    try {
      const response: AppStatus = await this.producerService.getApplicationStatusCount(userId, this.marketType);
      const parsedResponse = {'IN PROGRESS': response[APP_STATUS.IN_PROGRESS], 'SUBMITTED': response[APP_STATUS.SUBMITTED]};
      this.graphDataSource.next(response);
      
      if (this.marketType.toUpperCase() === "INDIVIDUAL" ) { 
        this.totalApplications = parsedResponse[APP_STATUS.SUBMITTED] + parsedResponse[APP_STATUS.SUBMITTED_FOR_APPROVAL];
        this.statuses.push(APP_STATUS.SUBMITTED);
        this.statuses.push(APP_STATUS.SUBMITTED_FOR_APPROVAL);
      } else {
        for (const [key, value] of Object.entries(parsedResponse)) {
          this.totalApplications = this.totalApplications + Number(value);
          const keyLabel = this.getStatusLabel(key);
          this.statuses.push(keyLabel);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  setPageCounts() {
    if (this.lastAppNo > this.totalApplications) {
      this.lastAppNo = this.totalApplications;
    }
    this.totalPages = Math.ceil(this.totalApplications / this.plansPerPage);
  }

  async sortPlans($event: any) {
    this.resetInitialNumbers();

    this.searchParams = {
      ...this.searchParams,
      productType: this.marketType,
      pageNumber: this.currentPage - 1,
      countPerPage: this.plansPerPage
    }

    if ($event) {
      switch ($event.value) {
        case 'Most Recently Submitted': {
          this.searchParams.sortingField = "submissionDate";
          this.searchParams.sortingOrder = "asc";
          break;
        }
        case 'Name (Descending)': {
          this.searchParams.sortingField = "name";
          this.searchParams.sortingOrder = "desc"
          break;
        }
        case 'Name (Ascending)': {
          this.searchParams.sortingField = "name";
          this.searchParams.sortingOrder = "asc"
          break;
        }
        default: {
          this.searchParams.sortingField = undefined;
          this.searchParams.sortingOrder = undefined
        }
      }
    }

    this.planSearchMessage = 'Sorting the Applications...';

    try {
      let response: SearchResponse = await this.getApplicationsBySearchParams();
      this.totalApplications = response.totalApplicationCount;
      this.setPageCounts();
      this.plans = [];
      Array.prototype.push.apply(this.plans, response.searchedApplications);
    } catch (error) {
      console.error(error);
    }
  }

  refreshPlansPerPage($event: any) {
    this.resetInitialNumbers();
    this.totalPages = Math.ceil(this.totalApplications / this.plansPerPage);
    this.search(false);
    this.resetAppNo();
  }

  prev() {
    if (this.currentPage > 1) {
      --this.currentPage;
      this.resetAppNo();
      this.search(false);
    }
  }

  next() {
    if (this.currentPage < this.totalPages) {
      ++this.currentPage;
      this.resetAppNo();
      this.search(false);
    }
  }

  resetAppNo() {
    this.firstAppNo = this.plansPerPage * (this.currentPage - 1) + 1;
    this.lastAppNo = this.plansPerPage * this.currentPage;
  }

  clearSearch() {
    this.appNumber = '';
    this.searchForm.reset();
    this.search(true);
  }

  resetInitialNumbers() {
    this.currentPage = 1;
    this.totalApplications = 0;
    this.totalPages = 0;
    this.firstAppNo = 1;
  }

  async search(isInitialLoad: boolean) {
    if (isInitialLoad) {
      this.resetInitialNumbers();
    }

    let combinedStatusByMarketType;
    if (this.marketType.toUpperCase() === "INDIVIDUAL" ) {
      combinedStatusByMarketType = environment.initData.brand.ASPID === "MN" ? `${APP_STATUS.SUBMITTED_TO_MN}, ${APP_STATUS.SUBMITTED_FOR_APPROVAL}` : APP_STATUS.SUBMITTED;
    } else {
      combinedStatusByMarketType = this.searchForm.controls['status'].value ? this.searchForm.controls['status'].value : undefined
    }

    try {

      let fromDate = null;
      if (this.searchForm.controls['fromDate'].value) {
        fromDate
          = this.dateUtility.getDateAsFormattedString(
            this.searchForm.controls['fromDate'].value,
            'MM/DD/YYYY'
          );
      }

      let toDate = null;
      if (this.searchForm.controls['toDate'].value) {
        toDate = this.dateUtility.getDateAsFormattedString(
          this.searchForm.controls['toDate'].value,
          'MM/DD/YYYY'
        );
      }

      this.searchParams = {
        ...this.searchParams,
        productType: this.marketType,
        firstName: this.searchForm.controls['firstName'].value ?? undefined,
        lastName: this.searchForm.controls['lastName'].value ?? undefined,
        planName: this.searchForm.controls['planName'].value ?? undefined,
        fromDate: fromDate ? fromDate : undefined,
        toDate: toDate ? toDate : undefined,
        combinedStatus: combinedStatusByMarketType,
        applicationTrackingNumber: this.appNumber ?? undefined,
        pageNumber: this.currentPage - 1,
        countPerPage: this.plansPerPage,
      };

      if (environment.initData.brand.ASPID === 'MN'){
        this.searchParams.agencyId = this.agencyId;
      }

      this.planSearchMessage = 'Searching for Applications...';

      let response: SearchResponse = await this.getApplicationsBySearchParams();

      if (response.searchedApplications.length > 0) {
        this.planSearchMessage = 'Applications Found...';
      } else {
        this.planSearchMessage = 'No Applications Found.';
      }

      this.totalApplications = response.totalApplicationCount;
      this.setPageCounts();
      this.plans = [];
      Array.prototype.push.apply(this.plans, response.searchedApplications);
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  async getApplicationsBySearchParams() {
    return await this.appStatusRetrieveService.getApplications(this.searchParams);
  }

  returnConfigAsBoolean(configValue) {
    return configValue === 'true' ? true : false;
  }

  statusDataMapLabels(statusData) {

    for (let statusKey in statusData) {
      if (statusData.hasOwnProperty(statusKey)) {
        const statusLabel = this.getStatusLabel(statusKey);
        statusData[statusLabel] = statusData[statusKey];
        if (statusLabel !== statusKey) {
          delete statusData[statusKey];
        }
      }
    }

    return statusData;

  }

  getStatusLabel(key) {
    const statusMap: string = STATUS_MAP[key];
    if (statusMap) {
      return statusMap;
    }
    return key;
  }
}
